import { useAuth0 } from "@auth0/auth0-react";
import { Home, LogOut, ScanSearch } from "lucide-react";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import tw from "twin.macro";

import BottomSheet, { useBottomSheetStack } from "@/components/BottomSheet";
import { SheetsEnum } from "@/sheets/enums/sheets.enum";
import { switchSheetsHeaderComponents } from "@/sheets/switchs/switch-sheets-header-components";
import { switchSheetsLeadIcons } from "@/sheets/switchs/switch-sheets-lead-icons";
import { SwitchSheetsRender } from "@/sheets/switchs/switch-sheets-render";
import { switchSheetsTitles } from "@/sheets/switchs/switch-sheets-titles";
import { SheetsType } from "@/sheets/types/sheets.type";

/** Couleur de fond commune à la barre et au bouton scan central. */
const NAV_BG = "rgba(20, 109, 66, 0.7)";

const BottomNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const { t } = useTranslation();

  // Stack de sheets — instance locale alimentée par les actions de la nav.
  // Le runtime context d'une parcelle (areaStateAggregate, areaCommandTracking)
  // n'est pas dispo ici, donc seuls les sheets niveau application qui n'en
  // dépendent pas (ex. SCAN_MODULE) doivent être poussés depuis cet endroit.
  const sheet = useBottomSheetStack<SheetsType>();

  // Sécurité : si la route change pour une raison externe, on referme le sheet.
  useEffect(() => {
    sheet.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <Fragment>
      <nav
        aria-label={t("navigation.home")}
        css={[
          tw`fixed bottom-0 left-0 right-0 z-40`,
          tw`backdrop-blur-md`,
          tw`pb-[env(safe-area-inset-bottom)]`,
          { backgroundColor: NAV_BG },
        ]}
      >
        {/* Version de l'app — petit texte blanc centré tout en bas. La valeur
            est injectée au build depuis `package.json` via la constante
            globale `__APP_VERSION__` (cf. vite.config.ts). */}
        <span
          aria-hidden="true"
          tw="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] leading-none text-white/70 select-none pointer-events-none"
        >
          v{__APP_VERSION__}
        </span>

        <ul tw="flex items-center justify-around relative h-16 px-4">
          {/* Accueil */}
          <li>
            <button
              type="button"
              aria-label={t("navigation.home")}
              onClick={() => navigate("/")}
              css={[
                tw`flex items-center justify-center w-12 h-12 rounded-full text-white`,
                tw`hover:bg-white/10 active:scale-95 transition`,
                tw`focus:(outline-none ring-2 ring-white/40)`,
              ]}
            >
              <Home tw="w-6 h-6" />
            </button>
          </li>

          {/* Scan — pousse SCAN_MODULE sur la stack. */}
          <li tw="relative">
            <button
              type="button"
              aria-label={t("navigation.moduleAccess")}
              onClick={() =>
                sheet.open({
                  id: SheetsEnum.SCAN_MODULE,
                  canGoBack: false,
                })
              }
              css={[
                tw`flex items-center justify-center w-16 h-16 rounded-full text-white shadow-lg`,
                tw`-mt-8 ring-4 ring-white/30`,
                tw`hover:scale-105 active:scale-95 transition`,
                tw`focus:(outline-none ring-4 ring-white/60)`,
                { backgroundColor: NAV_BG },
              ]}
            >
              <ScanSearch tw="w-7 h-7" />
            </button>
          </li>

          {/* Déconnexion */}
          <li>
            <button
              type="button"
              aria-label={t("navigation.logout")}
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
              css={[
                tw`flex items-center justify-center w-12 h-12 rounded-full text-white`,
                tw`hover:bg-white/10 active:scale-95 transition`,
                tw`focus:(outline-none ring-2 ring-white/40)`,
              ]}
            >
              <LogOut tw="w-6 h-6" />
            </button>
          </li>
        </ul>
      </nav>

      {/* Sheet hôte — exact même formalisme que dans area-state-aggregate.tsx. */}
      <BottomSheet
        open={sheet.isOpen}
        direction={sheet.direction}
        viewKey={sheet.visibleView?.id}
        onClose={sheet.close}
        onBack={sheet.canGoBack ? sheet.pop : undefined}
        leadingIcon={switchSheetsLeadIcons({ sheet })}
        title={switchSheetsTitles({ sheet })}
        headerComponent={switchSheetsHeaderComponents({ sheet })}
        dismissOnOutsideClick
      >
        <SwitchSheetsRender sheet={sheet} />
      </BottomSheet>
    </Fragment>
  );
};

export default BottomNavigation;
